import { Button, Flex, Space, Spin, Table, Tag, type TableProps } from 'antd'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { TrashIcon } from '@phosphor-icons/react'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import { SchoolService } from '../../../../../services/SchoolService'
import type { School, SchoolUser } from '../../../../../types'
import { EditSchoolUserModal } from '../../components/EditSchoolUserModal'

export const UserTable = ({ school, refetch }: { school: School; refetch: () => Promise<void> }) => {
    const [selectedSchoolUser, setSelectedSchoolUser] = useState<SchoolUser | null>()

    const navigate = useNavigate()

    const confirm = useConfirmation()
    const { showNotification } = useNotifications()

    const handleRemove = async (userId: string) => {
        if (
            await confirm({
                title: 'Are you sure you want to remove this user?',
                confirmText: 'Remove'
            })
        ) {
            try {
                await SchoolService.removeUser(school.id, userId)
                showNotification({ title: 'User removed' })
                await refetch()
            } catch (err) {
                showNotification({ title: 'Error removing user', type: 'error' })
            }
        }
    }

    const columns: TableProps<SchoolUser>['columns'] = [
        {
            title: 'ID',
            key: 'id',
            render: (_, { user }) => <>{user.id}</>
        },
        {
            title: 'Name',
            key: 'name',
            render: (_, { user }) => <>{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : ''}</>
        },
        {
            title: 'Email',
            key: 'email',
            render: (_, { user }) => <>{user.email}</>
        },
        {
            title: 'School Role',
            dataIndex: 'role'
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, { user }) => <>{user.status}</>
        },
        {
            title: 'Subscriptions',
            dataIndex: 'subscriptions',
            render: (_, { schoolSubscriptionSeats }) => {
                const initialSchoolSubscriptions: string[] = school.schoolSubscriptions
                    .filter(
                        ss =>
                            ss.subscription.status !== 'CANCELLED' &&
                            ss.subscription.status !== 'COMPLETED' &&
                            schoolSubscriptionSeats?.some(seat => seat.schoolSubscriptionId === ss.id)
                    )

                    .map(
                        s =>
                            s.subscription.name ||
                            s.subscription.subscriptionProducts.map(sp => sp.product.key).join(', ')
                    )

                return initialSchoolSubscriptions.map(subscriptionProductNames => (
                    <Space key={subscriptionProductNames}>
                        <Tag>{subscriptionProductNames}</Tag>
                    </Space>
                ))
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, schoolUser) => (
                <Flex gap="middle" align="center">
                    <Button
                        type="link"
                        onClick={() => {
                            setSelectedSchoolUser(schoolUser)
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        type="link"
                        onClick={() => {
                            navigate(`/users/${schoolUser.user.id}`)
                        }}
                    >
                        View
                    </Button>
                    <TrashIcon
                        size={20}
                        color="red"
                        onClick={() => handleRemove(schoolUser.user.id)}
                        cursor="pointer"
                    />
                </Flex>
            )
        }
    ]

    if (!school) return <Spin />

    return (
        <>
            <Table
                columns={columns}
                dataSource={school.schoolUsers || []}
                rowKey="id"
                pagination={{ pageSize: 50 }}
                styles={{
                    content: {
                        overflow: 'scroll'
                    }
                }}
            />
            {selectedSchoolUser && (
                <EditSchoolUserModal
                    schoolUser={selectedSchoolUser}
                    onSuccess={refetch}
                    handleClose={() => setSelectedSchoolUser(null)}
                    availableSchoolSubscriptions={school.schoolSubscriptions}
                />
            )}
        </>
    )
}
